import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash, faEdit, faSave } from '@fortawesome/free-solid-svg-icons';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import axios from 'axios';

function LaundryImageManager({ bookingId }) {
    const userRole = localStorage.getItem('userRole');
    const [isEditMode, setIsEditMode] = useState(false);
    const [uploadType, setUploadType] = useState('before');
    const [selectedStainItem, setSelectedStainItem] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const [beforePhotos, setBeforePhotos] = useState([]);
    const [stainPhotos, setStainPhotos] = useState([]);
    const [damagePhotos, setDamagePhotos] = useState([]);
    const [packingPhotos, setPackingPhotos] = useState([]);
    const [laundryItems, setLaundryItems] = useState([]);

    const fetchPhotosByType = useCallback(async (type, setter) => {
        try {
            const res = await axios.get(`/api/attachments/laundry/${bookingId}/${type}`);
            setter(res.status === 200 ? res.data : []);
        } catch (err) {
            if (err.response?.status === 404) setter([]);
            else console.error(`Error fetching ${type} photos:`, err);
        }
    }, [bookingId]);

    const fetchAllPhotos = useCallback(() => {
        fetchPhotosByType('before', setBeforePhotos);
        fetchPhotosByType('stain', setStainPhotos);
        fetchPhotosByType('damage', setDamagePhotos);
        fetchPhotosByType('packing', setPackingPhotos);
    }, [fetchPhotosByType]);

    const fetchLaundryItems = useCallback(async () => {
        const res = await axios.get(`/api/laundry/${bookingId}/pickup-items`);
        setLaundryItems(res.data || []);
    }, [bookingId]);

    useEffect(() => {
        fetchAllPhotos();
        fetchLaundryItems();
    }, [fetchAllPhotos, fetchLaundryItems]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files).map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));
        setSelectedFiles(prev => [...prev, ...files]);
    };

    const handleFileUpload = async () => {
        if (selectedFiles.length === 0) return;

        const formData = new FormData();
        selectedFiles.forEach(({ file }) => formData.append('photos', file));

        if ((uploadType === 'stain' || uploadType === 'damage') && selectedStainItem) {
            const item = laundryItems.find(i => i.name === selectedStainItem);
            if (item) formData.append('laundry_item_id', item.laundry_item_id);
        }

        if (uploadType === 'packing') {
            formData.append('bags', 1);
        }

        try {
            setIsUploading(true);
            await axios.post(`/api/attachments/laundry/${bookingId}/${uploadType}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSelectedFiles([]);
            fetchPhotosByType(uploadType, getSetterByType(uploadType));
        } catch (err) {
            console.error('Upload error:', err);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeletePhoto = async (photoId, type) => {
        const confirmed = window.confirm(`Delete this ${type} photo?`);
        if (!confirmed) return;

        try {
            await axios.delete(`/api/attachments/laundry/${bookingId}/${photoId}`);
            fetchPhotosByType(type, getSetterByType(type));
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const getSetterByType = (type) => {
        const map = {
            before: setBeforePhotos,
            stain: setStainPhotos,
            damage: setDamagePhotos,
            packing: setPackingPhotos
        };
        return map[type] || (() => { });
    };

    const openModal = (index, type) => {
        const actualType = type === 'stained-damaged'
            ? (stainPhotos.includes(stainPhotos[index]) ? 'stain' : 'damage')
            : type;
        setUploadType(actualType);
        setCurrentIndex(index);
        setIsOpen(true);
    };

    const closeModal = () => setIsOpen(false);

    const getPhotosByType = (type) => {
        switch (type) {
            case 'before': return beforePhotos;
            case 'stain': return stainPhotos;
            case 'damage': return damagePhotos;
            case 'packing': return packingPhotos;
            default: return [];
        }
    };

    const imagesConfig = {
        before: beforePhotos,
        'stained-damaged': [...stainPhotos, ...damagePhotos],
        packing: packingPhotos
    };

    return (
        <div className="image-manager">
            {Object.entries(imagesConfig).map(([type, photos]) =>
                photos.length > 0 && (
                    <div key={type} className="photos-section">
                        <h3>
                            {type === 'stained-damaged' ? 'Stained / Damaged' : `${type.charAt(0).toUpperCase() + type.slice(1)} Photos`}
                        </h3>
                        <div className="photos-grid">
                            {photos.map((photo, index) => (
                                <div key={photo.id || index} className="photo-item">
                                    <img
                                        src={photo.image_url}
                                        alt={`${type} ${index}`}
                                        className="thumbnail"
                                        onClick={() => openModal(index, type)}
                                    />
                                    {(userRole === 'admin' || userRole === 'manager' || userRole === 'supervisor') && isEditMode && (
                                        <button className="delete-photo-button" onClick={() => handleDeletePhoto(photo.id, photo.type)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    )}
                                    {photo.item_name && (
                                        <span className={`photo-badge ${photo.type === 'stain' ? 'stain' : 'damage'}`}>
                                            {photo.item_name}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}

            {isOpen && (
                <div className="photo-modal-overlay" onClick={closeModal}>
                    <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="modal-x" onClick={closeModal}>&times;</button>
                        <ImageGallery
                            items={getPhotosByType(uploadType).map((photo) => ({
                                original: photo.image_url,
                                thumbnail: photo.image_url
                            }))}
                            startIndex={currentIndex}
                            showThumbnails={false}
                            showFullscreenButton={false}
                            showPlayButton={false}
                            slideDuration={400}
                            showNav={true}
                        />
                    </div>
                </div>
            )}

            {(userRole === 'admin' || userRole === 'manager' || userRole === 'supervisor') && (
                <div className="edit-photos-section">
                    <button className="add-note-button edit-photos-button" onClick={() => setIsEditMode(prev => !prev)}>
                        <FontAwesomeIcon icon={isEditMode ? faSave : faEdit} />
                        {isEditMode ? ' Save' : ' Edit Photos'}
                    </button>

                    {isEditMode && (
                        <div className="upload-area">
                            <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} className="upload-type-select">
                                <option value="before">Before</option>
                                <option value="stain">Stain</option>
                                <option value="damage">Damage</option>
                                <option value="packing">Packing</option>
                            </select>

                            {(uploadType === 'stain' || uploadType === 'damage') && (
                                <select
                                    value={selectedStainItem}
                                    onChange={(e) => setSelectedStainItem(e.target.value)}
                                    className="affected-item-select"
                                >
                                    <option value="">Select Affected Item</option>
                                    {laundryItems.map((item) => (
                                        <option key={item.laundry_item_id} value={item.name}>
                                            {item.name}
                                        </option>
                                    ))}
                                </select>
                            )}

                            <input
                                id="upload-input"
                                type="file"
                                multiple
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileSelect}
                            />
                            <button
                                className="upload-button"
                                onClick={() => document.getElementById('upload-input').click()}
                            >
                                <FontAwesomeIcon icon={faUpload} /> Upload Photos
                            </button>

                            <div className="photo-preview-container">
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className="photo-preview">
                                        <img src={file.preview} alt={`Preview ${index}`} />
                                        <button type="button" onClick={() =>
                                            setSelectedFiles(prev => prev.filter((_, i) => i !== index))
                                        }>X</button>
                                    </div>
                                ))}
                            </div>

                            {selectedFiles.length > 0 && (
                                <button className="confirm-upload-apartment" onClick={handleFileUpload} disabled={isUploading}>
                                    {isUploading ? 'Uploading...' : 'Confirm Upload'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default LaundryImageManager;